/**
 * The evidence "Ask Iris" sends from the price analyzers (CU-869fkdhjv phase 3, plan § 2.4): DOR, LOR, Direct
 * Site, Brokers and the Rank Analyzer. Each builder takes what the screen already holds — the analyzer response,
 * what the chart paints, the KPI cards' figures — and returns `{ kind, labels, subject?, state }`: the question's
 * kind and labels (`@utils/askIris/questions`) and the `canvas_state` (`@utils/askIris/canvasState`). Nothing is
 * fetched and nothing is recomputed that the screen did not compute (criterion 6).
 *
 * The analyzers' figures, as the cells show them (`Table/columns/Data/@components/{PriceContent,ComparisonContent}`):
 * the price; the previous price and its change in % with the arrow; "Vs: <reference>" with the absolute %; green /
 * red = cheapest / dearest of the column (`isCheapestColumn` / `isMoreExpensiveColumn`, webback
 * `base_dashboard_service.py:898-908`).
 *
 * DS Repricing (criterion 26): the decisions of a cell travel only with `dsRepricing` and the decisions switch on
 * (what is painted, `cellDecisions.on`), and `canvasState` strips anything of the engine otherwise.
 *
 * Pure and alias-free (TL R4): relative imports only.
 */
import { canvasState, captureAge } from '../../../@utils/askIris/canvasState.js'
import { KIND_LABELS, isSim, limitOf, priceLine, resultOf, sortDecisions } from '../@components/Table/columns/Data/@services/cellDecisions.js'
import { pickLabel, pickOn } from './carPick.js'
import { isMainKey, keepMain } from './mainSeries.js'

export const SCREENS = {
	dor: 'Dates of Rental Analyzer',
	lor: 'Length of Rental Analyzer',
	directSite: 'Direct Site Analyzer',
	brokers: 'Brokers Analyzer',
	position: 'Rank Analyzer',
}

const ROW_META_KEYS = ['name', 'nameFilter', 'nameIcon', 'nameMarket', 'isMainRow', 'avgPrice']
const MAX_DECISIONS = 3
const MAX_CHART_SERIES = 8
const MAX_EVOLUTION_SERIES = 6
const MAX_RANK_BRANDS = 8
const MAX_BAND_DATES = 14

const isNum = v => typeof v === 'number' && Number.isFinite(v)
const num = v => (v === '' || v == null ? null : (Number.isFinite(Number(v)) ? Number(v) : null))
const round2 = v => (isNum(v) ? Math.round(v * 100) / 100 : v)

// drop null / undefined / empty arrays and objects, so the evidence carries only what exists
function compact(obj) {
	const out = {}
	for (const [k, v] of Object.entries(obj || {})) {
		if (v == null || v === '') continue
		if (Array.isArray(v) && !v.length) continue
		if (!Array.isArray(v) && typeof v === 'object' && !Object.keys(v).length) continue
		out[k] = v
	}
	return out
}

export const rowLabelOf = row => row?.nameFilter || row?.name || null
export const isMainRow = row => row?.isMainRow ?? Object.entries(row || {})
	.some(([k, v]) => !ROW_META_KEYS.includes(k) && v && typeof v === 'object' && v.is_main_row)

const hasPrice = cell => !!cell && !cell.noResult && isNum(num(cell.price))

// ---------------------------------------------------------------------------
// Scope and freshness
// ---------------------------------------------------------------------------

/** Freshness of an analyzer answer: the execution's capture time (🧭 4-A: no per-cell scarcity). */
export function freshnessOf(executionDateTime, now = new Date()) {
	return compact({ data_source: 'live', captured_at: executionDateTime || null, capture_age: captureAge(executionDateTime, now) })
}

/** The Cars selector's pick (CU-869fmpqfm) as the scope says it — `Acriss CDMR`, `Group Compact` — or null on All cars. */
export function carPickOf(pick) {
	return pickOn(pick) ? { kind: pick.kind, label: pickLabel(pick) } : null
}

const ID_VALUE = v => typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean'

/**
 * `params` of every analyzer element: the scope as labels (office, capture, Min/Max, Total/Daily, currency,
 * reference, columns in view, the pass with DS Repricing, the car pick of CU-869fmpqfm when the screen has one)
 * and the request echo as ids (`res.filters`).
 */
export function scopeParams({
	office, executionDateTime, passLabel, columns = [], currency, minMax, daily, mainName, lor, date, filters, carPick,
} = {}) {
	const cols = (columns || []).filter(c => c != null && c !== '')
	const ids = {}
	if (filters && typeof filters === 'object' && !Array.isArray(filters)) {
		for (const [k, v] of Object.entries(filters)) {
			if (ID_VALUE(v)) ids[k] = v
			else if (Array.isArray(v) && v.length && v.every(ID_VALUE)) ids[k] = v
		}
	}
	return compact({
		scope: compact({
			office: office?.name || null,
			office_id: office?.officeID ?? null,
			captured_at: executionDateTime || null,
			repricing_pass: passLabel || null,
			table: minMax ? (String(minMax).toLowerCase().startsWith('max') ? 'Max' : 'Min') : null,
			price: daily == null ? null : (daily ? 'daily' : 'total'),
			currency: currency || null,
			reference: mainName || null,
			length_days: lor ?? null,
			pickup_date: date ?? null,
			columns: cols.length ? compact({ first: cols[0], last: cols[cols.length - 1], count: cols.length }) : null,
			car_pick: carPick || null,
		}),
		ids,
	})
}

// ---------------------------------------------------------------------------
// The price cell (DOR, LOR, Direct Site, Brokers — main table)
// ---------------------------------------------------------------------------

/** The column of the table the cell sits in: who is cheapest and dearest, this cell's rank, the own price. */
export function columnDigest(rows = [], columnKey, cell) {
	const priced = (rows || [])
		.map(row => ({ row, cell: row?.[columnKey] }))
		.filter(({ cell: c }) => hasPrice(c))
		.map(({ row, cell: c }) => ({ label: rowLabelOf(row), price: num(c.price), main: isMainRow(row) }))
	if (!priced.length) return null
	const sorted = [...priced].sort((a, b) => a.price - b.price)
	const cheapest = sorted[0], dearest = sorted[sorted.length - 1]
	const own = priced.find(p => p.main)
	const price = num(cell?.price)
	return compact({
		cheapest: { label: cheapest.label, price: cheapest.price },
		dearest: { label: dearest.label, price: dearest.price },
		own: own ? { label: own.label, price: own.price } : null,
		// 1 = cheapest; ties share a rank, as the green / red marks do
		rank: isNum(price) ? 1 + priced.filter(p => p.price < price).length : null,
		rows_priced: priced.length,
	})
}

const carOf = priceData => priceData?.id_acriss || priceData?.acriss || priceData?.id_category || null

/** A decision as the cell's detail lists it (`Popover/Decisions.jsx`), never more than the screen says. */
function decisionLine(d, currency) {
	return compact({
		type: KIND_LABELS[d.event_type] || d.event_type,
		acriss: d.scope?.sipp || null,
		strategy: d.strategy_name || null,
		prices: priceLine(d, currency) || null,
		limit: limitOf(d) || null,
		result: resultOf(d)?.label || null,
		simulated: isSim(d) || null,
	})
}

/**
 * `{ kind, labels, state }` of a price cell. `cell` is the response's cell (`trows[i][column]`), `column` its
 * column key (a pick-up date in DOR, a length in LOR, a category in Direct Site / Brokers), `rowLabel` what the
 * row says. `decisions` only the ones painted (switch on); they travel only with `dsRepricing`.
 */
export function cellEvidence({
	section = 'dor', cell, column, rowLabel, rows = [], columns = [], currency, daily, minMax, decisions = [],
	dsRepricing = false, params = {}, freshness = {},
}) {
	if (!hasPrice(cell)) return null
	const pd = cell.price_data || {}
	const cur = pd.id_master_currency_translation || currency || null
	const shown = dsRepricing ? sortDecisions(decisions || []).slice(0, MAX_DECISIONS) : []
	const isDate = section === 'dor'
	const isLength = section === 'lor'
	const date = pd.pickup_date ?? (isDate ? column : null)
	const days = pd.length_of_rental ?? (isLength ? column : null)
	const car = carOf(pd) || (!isDate && !isLength ? column : null)
	const row = rowLabel || cell.name || null

	const selection = compact({
		row,
		column: String(column ?? ''),
		car,
		car_description: pd.car_description || null,
		pickup_date: date,
		length_days: days,
		price: num(cell.price),
		currency: cur,
		price_basis: daily == null ? null : (daily ? 'daily' : 'total'),
		table: minMax ? (String(minMax).toLowerCase().startsWith('max') ? 'Max' : 'Min') : null,
		previous_price: num(cell.previous_price),
		change_pct: cell.evolutionChange ? num(cell.evolutionDiff) : null,
		change: cell.evolutionChange || null,
		vs: cell.main_name && cell.comparison_type && cell.comparison_type !== 'noComparison'
			? compact({ reference: cell.main_name, diff_pct: isNum(num(cell.diff_price)) ? Math.abs(num(cell.diff_price)) : null, reference_is: cell.comparison_type })
			: null,
		cheapest_in_column: cell.isCheapestColumn || null,
		dearest_in_column: cell.isMoreExpensiveColumn || null,
		decisions: shown.length ? shown.map(d => decisionLine(d, cur || '€')) : null,
	})

	const digest = compact({
		column: columnDigest(rows, column, cell),
		table: { rows: (rows || []).length, columns: (columns || []).length },
	})

	const kind = shown.length ? 'cellDecisions' : 'cell'
	return {
		kind,
		labels: [row, car, date != null ? { date } : null, days != null ? { days } : null],
		state: canvasState({
			view: `analyzer.${section}`, screen: SCREENS[section], selection, digest, params, freshness,
			definition: kind, dsRepricing,
		}),
	}
}

/** "Price Table sorted by Categories" (Direct Site, Brokers): one category per column, cheapest first. */
export function categoryCellEvidence({ section = 'directSite', category, cell, rows = [], currency, params = {}, freshness = {}, dsRepricing = false }) {
	const price = num(cell?.value)
	if (!isNum(price)) return null
	const who = cell.id_master_provider_translation || cell.id_site_translation || null
	const column = (rows || [])
		.map(r => r?.[category])
		.filter(c => isNum(num(c?.value)))
		.map(c => ({ label: c.id_master_provider_translation || c.id_site_translation || null, price: num(c.value) }))
		.sort((a, b) => a.price - b.price)
	const pd = cell.price_data || {}
	return {
		kind: 'cell',
		labels: [who, category, pd.pickup_date ? { date: pd.pickup_date } : null, pd.length_of_rental != null ? { days: pd.length_of_rental } : null],
		state: canvasState({
			view: `analyzer.${section}`, screen: SCREENS[section],
			selection: compact({ table: 'Price Table sorted by Categories', category, offer_by: who, price, currency: currency || null, car_description: pd.car_description || null }),
			digest: compact({
				column: column.length ? compact({
					cheapest: column[0], dearest: column[column.length - 1],
					rank: 1 + column.filter(c => c.price < price).length, rows_priced: column.length,
				}) : null,
				table: { rows: (rows || []).length },
			}),
			params, freshness, definition: 'cell', dsRepricing,
		}),
	}
}

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

const valuesOf = (data, key) => (data || []).map(r => num(r?.[key])).filter(isNum)

function seriesSummary(data, key) {
	const values = valuesOf(data, key)
	if (!values.length) return null
	return { label: key, min: round2(Math.min(...values)), max: round2(Math.max(...values)), last: round2(values[values.length - 1]) }
}

/**
 * The analyzer chart, from what it paints (plan-review Obs. 4): `data` = `filteredChart` (the site filter
 * applied), `series` = `seriesToApply` of the active mode, `visible` = `analyzerChartVisibility`. Variation mode →
 * the values are %. No visible series → null (the no-series card has no button, C5). `mainKeys`: the main
 * provider's / main site's series (`mainSeries.js`), always among the listed ones and flagged `main` (CU-869fmm949).
 */
export function chartEvidence({
	section = 'dor', title, mode = 'price', siteFilter, data = [], series = [], visible = [], currency, labels = [],
	params = {}, freshness = {}, dsRepricing = false, mainKeys = [],
}) {
	const shownKeys = (series || []).map(s => s?.dataKey).filter(k => k && (visible || []).includes(k))
	if (!shownKeys.length || !(data || []).length) return null
	const summaries = shownKeys.map(k => seriesSummary(data, k)).filter(Boolean).sort((a, b) => a.min - b.min)
		.map(s => (isMainKey(s.label, mainKeys) ? { ...s, main: true } : s))
	const variation = mode !== 'price'
	return {
		kind: 'chart',
		labels: [title, ...labels],
		state: canvasState({
			view: `analyzer.${section}`, screen: SCREENS[section],
			selection: compact({
				chart: title,
				mode: variation ? 'Variation (%)' : 'Price values',
				unit: variation ? '%' : (currency || null),
				site_filter: siteFilter || null,
				points: data.length,
				axis: compact({ first: data[0]?.date, last: data[data.length - 1]?.date }),
			}),
			digest: compact({
				series: keepMain(summaries, MAX_CHART_SERIES, x => !!x.main),
				series_not_listed: summaries.length > MAX_CHART_SERIES ? summaries.length - MAX_CHART_SERIES : null,
				hidden_series: (series || []).length - shownKeys.length || null,
			}),
			params, freshness, definition: 'chart', dsRepricing,
		}),
	}
}

/**
 * The Evolution Chart of a cell (or of a column, from its header): per visible series the first, last, min, max
 * and change over the capture days — the x axis is the capture day, not the pick-up date (criterion 7).
 */
export function evolutionEvidence({
	section = 'dor', origin = {}, data = [], series = [], visible = [], currency, params = {}, freshness = {}, dsRepricing = false,
}) {
	const keys = (series || []).map(s => s?.dataKey).filter(k => k && (visible || []).includes(k))
	if (!keys.length || !(data || []).length) return null
	const lines = keys.map(k => {
		const values = valuesOf(data, k)
		if (!values.length) return null
		const first = values[0], last = values[values.length - 1]
		return {
			label: k, first: round2(first), last: round2(last), min: round2(Math.min(...values)), max: round2(Math.max(...values)),
			change: round2(last - first), change_pct: first ? round2((last / first - 1) * 100) : null,
		}
	}).filter(Boolean)
	const { row, car, date, days, from = 'cell' } = origin || {}
	return {
		kind: 'evolution',
		labels: [row, car, date != null ? { date } : null, days != null ? { days } : null],
		state: canvasState({
			view: `analyzer.${section}`, screen: SCREENS[section],
			selection: compact({
				chart: 'Evolution Chart', opened_from: from, row: row || null, car: car || null, pickup_date: date ?? null, length_days: days ?? null,
				axis: 'capture day', currency: currency || null,
				window: compact({ first: data[0]?.date, last: data[data.length - 1]?.date, captures: data.length }),
			}),
			digest: compact({
				series: lines.slice(0, MAX_EVOLUTION_SERIES),
				series_not_listed: lines.length > MAX_EVOLUTION_SERIES ? lines.length - MAX_EVOLUTION_SERIES : null,
				hidden_series: (series || []).length - keys.length || null,
			}),
			params, freshness, definition: 'evolution', dsRepricing,
		}),
	}
}

// ---------------------------------------------------------------------------
// Rank Analyzer
// ---------------------------------------------------------------------------

// The gap as the Rank cell prints it (`ComparisonContent` compact): the sign reads from the main brand's side
export function gapText(cell) {
	const diff = num(cell?.diff_price)
	if (!isNum(diff) || !cell?.comparison_type || cell.comparison_type === 'noComparison') return null
	const sign = cell.comparison_type === 'more expensive' ? '−' : cell.comparison_type === 'cheaper' ? '+' : ''
	return `${sign}${Math.abs(diff)}%`
}

/** A brand × date cell of the Rank table: that date's cheapest brands, own price and rank, field size. */
export function rankCellEvidence({ cell, column, rowLabel, rows = [], mainName, currency, lor, params = {}, freshness = {} }) {
	if (!hasPrice(cell)) return null
	const priced = (rows || [])
		.map(row => ({ row, c: row?.[column] }))
		.filter(({ c }) => hasPrice(c))
		.map(({ row, c }) => ({ label: rowLabelOf(row), price: num(c.price), main: isMainRow(row) }))
		.sort((a, b) => a.price - b.price)
	const price = num(cell.price)
	const own = priced.find(p => p.main)
	const gap = gapText(cell)
	const brand = rowLabel || cell.name || null
	const reference = mainName || cell.main_name || null
	return {
		kind: 'rankCell',
		labels: [brand, { date: column }, gap && reference ? { value: `${gap} vs ${reference}` } : null],
		state: canvasState({
			view: 'analyzer.position', screen: SCREENS.position,
			selection: compact({
				brand, pickup_date: column, length_days: lor ?? null, price, currency: cell.price_data?.id_master_currency_translation || currency || null,
				gap_vs_reference: gap, reference,
				rank: 1 + priced.filter(p => p.price < price).length,
			}),
			digest: compact({
				date: compact({
					cheapest: priced.slice(0, MAX_RANK_BRANDS).map(({ label, price: p }) => ({ label, price: p })),
					own: own ? { label: own.label, price: own.price, rank: 1 + priced.filter(p => p.price < own.price).length } : null,
					field: priced.length,
				}),
			}),
			params, freshness, definition: 'rankCell', dsRepricing: false,
		}),
	}
}

const kpiLine = k => `${k.label}: ${k.value}${k.unit ? ` ${k.unit}` : ''}${k.note ? ` — ${k.note}` : ''}`

/** One of the five KPI cards, from `buildPositionKpis` output — the very figures on the card (T16). */
export function kpiEvidence({ kpis = [], key, labels = [], params = {}, freshness = {} }) {
	const kpi = (kpis || []).find(k => k.key === key)
	if (!kpi || kpi.value == null || kpi.value === '-') return null
	return {
		kind: 'kpi',
		subject: kpi.label,
		labels: [{ value: `${kpi.value}${kpi.unit ? ` ${kpi.unit}` : ''}` }, ...labels],
		state: canvasState({
			view: 'analyzer.position', screen: SCREENS.position,
			selection: compact({ kpi: kpi.label, value: kpi.value, unit: kpi.unit || null, note: kpi.note || null }),
			digest: { other_kpis: kpis.filter(k => k.key !== key).map(kpiLine) },
			params, freshness, definition: `kpi.${key}`, dsRepricing: false,
		}),
	}
}

/** The "Read:" sentence as the screen composes it (`PositionVerdict`), from `buildVerdict` output. */
export function verdictSentence(verdict) {
	if (!verdict) return null
	const parts = [`${verdict.mainName} sits`]
	if (verdict.deltaText) parts.push(verdict.deltaText)
	parts.push('vs the blended market average')
	if (verdict.rankText) parts.push(`and ranks ${verdict.rankText}`)
	let text = parts.join(' ')
	if (verdict.cheapestText) text += ` · ${verdict.cheapestText}`
	text += `. ${verdict.underText}`
	if (verdict.stanceText) text += ` ${verdict.stanceText}`
	return text
}

export function readingEvidence({ verdict, kpis = [], labels = [], params = {}, freshness = {} }) {
	const sentence = verdictSentence(verdict)
	if (!sentence) return null
	return {
		kind: 'reading',
		labels,
		state: canvasState({
			view: 'analyzer.position', screen: SCREENS.position,
			selection: { reading: sentence },
			digest: { kpis: (kpis || []).map(kpiLine) },
			params, freshness, definition: 'reading', dsRepricing: false,
		}),
	}
}

/** "Position over time": per pick-up date the competitors' range, the market average and the brand's price. */
export function bandEvidence({ title = 'Position over time', rows = [], mainKey, labels = [], params = {}, freshness = {} }) {
	const dated = (rows || []).filter(r => r && r.date != null)
	if (!dated.length || !mainKey) return null
	const lines = dated.map(r => compact({
		date: r.date, own: num(r.main), rank: num(r.rank), brands: num(r.brands),
		min: num(r.minPrice), avg: round2(num(r.avgPrice)), max: num(r.maxPrice),
	}))
	const owned = lines.filter(l => isNum(l.own))
	return {
		kind: 'chart',
		labels: [title, ...labels],
		state: canvasState({
			view: 'analyzer.position', screen: SCREENS.position,
			selection: { chart: title, brand: mainKey, axis: 'pick-up date' },
			digest: compact({
				dates: lines.slice(0, MAX_BAND_DATES),
				dates_not_listed: lines.length > MAX_BAND_DATES ? lines.length - MAX_BAND_DATES : null,
				dates_with_own_price: owned.length,
				dates_below_market_average: owned.filter(l => isNum(l.avg) && l.own < l.avg).length,
			}),
			params, freshness, definition: 'band', dsRepricing: false,
		}),
	}
}

/** "Rank evolution": the average rank of each visible brand over the pick-up dates, best first. */
export function rankChartEvidence({ title = 'Rank evolution', rows = [], series = [], visible = [], mainKey, labels = [], params = {}, freshness = {} }) {
	const keys = (series || []).map(s => s?.dataKey).filter(k => k && (visible || []).includes(k))
	if (!keys.length || !(rows || []).length) return null
	const brands = keys.map(k => {
		const ranks = valuesOf(rows, k)
		if (!ranks.length) return null
		const label = (series.find(s => s.dataKey === k)?.label) || k
		return compact({ label, avg_rank: round2(ranks.reduce((a, b) => a + b, 0) / ranks.length), best: Math.min(...ranks), dates: ranks.length, main: k === mainKey || null })
	}).filter(Boolean).sort((a, b) => a.avg_rank - b.avg_rank)
	return {
		kind: 'chart',
		labels: [title, ...labels],
		state: canvasState({
			view: 'analyzer.position', screen: SCREENS.position,
			selection: compact({ chart: title, brand: mainKey || null, axis: 'pick-up date', rank: '1 = cheapest' }),
			digest: compact({
				brands: brands.slice(0, MAX_RANK_BRANDS),
				brands_not_listed: brands.length > MAX_RANK_BRANDS ? brands.length - MAX_RANK_BRANDS : null,
				hidden_series: (series || []).length - keys.length || null,
			}),
			params, freshness, definition: 'rankChart', dsRepricing: false,
		}),
	}
}
